import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const SyncContext = createContext({ tick: 0 });

// Lightweight real-time sync: polls a global data-version (audit-log count, which
// bumps on every transaction). On change it invalidates all TanStack queries so every
// active page refetches automatically, and bumps `tick` for non-query refresh keys.
export function SyncProvider({ children }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tick, setTick] = useState(0);
  const verRef = useRef(null);

  useEffect(() => {
    if (!user) return undefined;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/sync/version");
        if (stopped) return;
        if (verRef.current === null) { verRef.current = data.version; return; }
        if (data.version !== verRef.current) {
          verRef.current = data.version;
          setTick((t) => t + 1);
          qc.invalidateQueries();
        }
      } catch { /* ignore transient/unauthenticated errors */ }
    };
    poll();
    const iv = setInterval(poll, 3500);
    const onFocus = () => { if (document.visibilityState !== "hidden") poll(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopped = true;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user, qc]);

  return <SyncContext.Provider value={{ tick }}>{children}</SyncContext.Provider>;
}

export const useSync = () => useContext(SyncContext);
